import { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { uploadImage, deleteImage } from "../../cloudinary";
import type { HeroSlide } from "../../types";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  IconButton,
  Image,
  Spinner,
  useToast,
  Switch,
  FormControl,
  FormLabel,
  SimpleGrid,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon } from "@chakra-ui/icons";

const AdminHeroSlides = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const fetchSlides = async () => {
    const snap = await getDocs(query(collection(db, "heroSlides"), orderBy("order", "asc")));
    setSlides(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as HeroSlide[]);
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleAddSlide = async (file: File) => {
    setIsUploading(true);
    try {
      const { url, publicId } = await uploadImage(file);
      await addDoc(collection(db, "heroSlides"), {
        imageUrl: url,
        imagePublicId: publicId,
        headline: "New headline",
        subtext: "",
        ctaLabel: "",
        ctaLink: "",
        order: slides.length,
        active: true,
      });
      toast({ title: "Slide added", status: "success", duration: 3000, isClosable: true });
      fetchSlides();
    } catch {
      toast({ title: "Error uploading image", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpdate = async (slide: HeroSlide, field: keyof HeroSlide, value: string | boolean) => {
    setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, [field]: value } : s)));
    await updateDoc(doc(db, "heroSlides", slide.id), { [field]: value });
  };

  const handleDelete = async (slide: HeroSlide) => {
    await deleteImage(slide.imagePublicId).catch(() => {});
    await deleteDoc(doc(db, "heroSlides", slide.id));
    fetchSlides();
  };

  return (
    <VStack spacing={8} align="stretch">
      <HStack justify="space-between" wrap="wrap">
        <Heading fontSize="22px" color="rs.text">
          Hero Carousel Slides
        </Heading>
        <Box>
          <Input
            type="file"
            accept="image/*"
            display="none"
            ref={fileInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleAddSlide(file);
            }}
          />
          <Button
            colorScheme="red"
            leftIcon={isUploading ? <Spinner size="sm" /> : <AddIcon />}
            onClick={() => fileInputRef.current?.click()}
            isDisabled={isUploading}
          >
            Add slide
          </Button>
        </Box>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {slides.map((slide) => (
          <Box key={slide.id} bg="rs.surface" border="1px solid" borderColor="rs.border" borderRadius="10px" p={5}>
            <Image src={slide.imageUrl} alt={slide.headline} borderRadius="8px" mb={4} h="140px" w="100%" objectFit="cover" />

            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="12px">Headline</FormLabel>
                <Input value={slide.headline} onChange={(e) => handleUpdate(slide, "headline", e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px">Subtext</FormLabel>
                <Textarea
                  size="sm"
                  value={slide.subtext ?? ""}
                  onChange={(e) => handleUpdate(slide, "subtext", e.target.value)}
                />
              </FormControl>
              <HStack>
                <FormControl>
                  <FormLabel fontSize="12px">CTA label</FormLabel>
                  <Input
                    size="sm"
                    value={slide.ctaLabel ?? ""}
                    onChange={(e) => handleUpdate(slide, "ctaLabel", e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="12px">CTA link</FormLabel>
                  <Input
                    size="sm"
                    placeholder="/category/cameras"
                    value={slide.ctaLink ?? ""}
                    onChange={(e) => handleUpdate(slide, "ctaLink", e.target.value)}
                  />
                </FormControl>
              </HStack>
              <HStack justify="space-between">
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel fontSize="12px" mb={0} mr={2}>
                    Active
                  </FormLabel>
                  <Switch
                    isChecked={slide.active}
                    onChange={(e) => handleUpdate(slide, "active", e.target.checked)}
                    colorScheme="red"
                  />
                </FormControl>
                <IconButton aria-label="Delete slide" icon={<DeleteIcon />} size="sm" onClick={() => handleDelete(slide)} />
              </HStack>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {slides.length === 0 && <Text color="rs.textMuted">No hero slides yet — add one above.</Text>}
    </VStack>
  );
};

export default AdminHeroSlides;
